import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams, useRouterState, useSearch } from '@tanstack/react-router'
import { MessageSquareIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@campai/lib-web/routes'
import { Button, Card, CardContent, CardHeader, DialogPrompt, Skeleton } from '@campai/lib-web/components/new/ui'
import { client, type RouterOutput } from '@campai/lib-web/utils/trpc'

import { Page } from '@/components/Page'
import { type AttachmentInput } from '@/product/community/shared/Attachments'
import { InlineContentComposer } from '@/product/community/shared/InlineContentComposer'
import { type Status } from '@/product/feedback/status'
import { canCreateFeedbackContent, canModerateFeedbackContent, isOwnFeedbackContent } from '@/product/feedback/access'
import { TicketDialog } from '@/product/feedback/shared/TicketDialog'
import { MetadataDialog } from '@/product/feedback/ticket/MetadataDialog'

import { Toolbar } from './Toolbar'
import { Comment } from './Comment'
import { Header } from './Header'

type TicketData = RouterOutput['product']['feedback']['getTicket']
type CommentData = RouterOutput['product']['feedback']['comments']['listComments']['comments'][number]

const COMMENT_PAGE_SIZE = 30
const targetCommentHashPattern = /^#?comment-([a-f\d]{24})$/i

const getTargetCommentId = (hash: string) => {
    const match = targetCommentHashPattern.exec(hash)
    return match?.[1] ?? null
}

export const Ticket = observer(() => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { ticketNumber } = useParams({ from: '/product/feedback/tickets/$ticketNumber' })
    const listSearch = useSearch({ from: '/product/feedback/tickets/$ticketNumber' })
    const hash = useRouterState({ select: (state) => state.location.hash })
    const [ticket, setTicket] = useState<TicketData | null>(null)
    const [comments, setComments] = useState<CommentData[] | null>(null)
    const [commentCount, setCommentCount] = useState<number | null>(null)
    const [ticketDialogOpen, setTicketDialogOpen] = useState(false)
    const [ticketMetadataDialogOpen, setTicketMetadataDialogOpen] = useState(false)
    const [editingComment, setEditingComment] = useState<CommentData | null>(null)
    const [ticketDeleteOpen, setTicketDeleteOpen] = useState(false)
    const [commentToDelete, setCommentToDelete] = useState<CommentData | null>(null)
    const [reloadVersion, setReloadVersion] = useState(0)
    const [isVoting, setIsVoting] = useState(false)
    const [isFollowUpdating, setIsFollowUpdating] = useState(false)
    const [isLoadingMoreComments, setIsLoadingMoreComments] = useState(false)
    const requestVersionRef = useRef(0)
    const scrolledTargetCommentRef = useRef<string | null>(null)
    const targetCommentId = getTargetCommentId(hash)
    const number = Number(ticketNumber)
    const canCreateComment = canCreateFeedbackContent()
    const canManage = canModerateFeedbackContent()
    const canEditTicket = !!ticket && (canManage || isOwnFeedbackContent(ticket.createdBy))
    const canDeleteTicket = canEditTicket
    const hasMoreComments = !!comments && commentCount !== null && comments.length < commentCount

    useEffect(() => {
        const abortController = new AbortController()
        const requestVersion = requestVersionRef.current + 1
        requestVersionRef.current = requestVersion

        setTicket(null)
        setComments(null)
        setCommentCount(null)

        if (!Number.isInteger(number) || number <= 0) {
            setComments([])
            setCommentCount(0)
            return () => abortController.abort()
        }

        void Promise.all([
            client.product.feedback.getTicket.query({ ticketNumber: number }, { signal: abortController.signal }),
            client.product.feedback.comments.listComments.query(
                {
                    ticketNumber: number,
                    limit: COMMENT_PAGE_SIZE,
                    offset: 0,
                    returnCount: true,
                    sort: { createdAt: 'asc' },
                },
                { signal: abortController.signal }
            ),
        ])
            .then(([ticketResult, commentsResult]) => {
                if (requestVersionRef.current !== requestVersion) {
                    return
                }

                setTicket(ticketResult)
                setComments(commentsResult.comments)
                setCommentCount(commentsResult.count ?? commentsResult.comments.length)
            })
            .catch(() => {
                if (!abortController.signal.aborted && requestVersionRef.current === requestVersion) {
                    setComments([])
                    setCommentCount(0)
                }
            })

        return () => abortController.abort()
    }, [number, reloadVersion])

    const reload = () => setReloadVersion((currentVersion) => currentVersion + 1)

    const toggleVote = async () => {
        if (!ticket || isVoting) {
            return
        }

        setIsVoting(true)

        try {
            const result = await client.product.feedback.toggleTicketVote.mutate({ ticketNumber: ticket.number })
            setTicket({
                ...ticket,
                votedByCurrentUser: result.votedByCurrentUser,
                votesCount: result.votesCount,
            })
        } finally {
            setIsVoting(false)
        }
    }

    const setTicketFollowed = async (followed: boolean) => {
        if (!ticket || isFollowUpdating) {
            return
        }

        setIsFollowUpdating(true)

        try {
            if (followed) {
                await client.product.feedback.followTicket.mutate({
                    ticketNumber: ticket.number,
                })
            } else {
                await client.product.feedback.unfollowTicket.mutate({
                    ticketNumber: ticket.number,
                })
            }

            setTicket({ ...ticket, followedByCurrentUser: followed })
        } finally {
            setIsFollowUpdating(false)
        }
    }

    const updateTicket = async (input: { title: string; message: string; attachments: AttachmentInput }) => {
        if (!ticket) {
            return
        }

        await client.product.feedback.updateTicket.mutate({
            ticketNumber: ticket.number,
            ...input,
        })
        reload()
    }

    const updateTicketMetadata = async (input: { status: Status; tags: string[] }) => {
        if (!ticket) {
            return
        }

        await client.product.feedback.updateTicketMetadata.mutate({
            ticketNumber: ticket.number,
            ...input,
        })
        setTicket({ ...ticket, status: input.status, tags: input.tags })
    }

    const deleteTicket = async () => {
        if (!ticket) {
            return
        }

        await client.product.feedback.deleteTicket.mutate({
            ticketNumber: ticket.number,
        })
        await navigate({ to: '/product/feedback', search: listSearch })
    }

    const createComment = async (input: { message: string; attachments: AttachmentInput }) => {
        const createdComment = await client.product.feedback.comments.createComment.mutate({
            ticketNumber: number,
            ...input,
        })
        setComments((currentComments) => [...(currentComments || []), createdComment])
        setCommentCount((currentCount) => (currentCount === null ? currentCount : currentCount + 1))
        setTicket((currentTicket) =>
            currentTicket
                ? {
                      ...currentTicket,
                      commentsCount: currentTicket.commentsCount + 1,
                      latestActivityAt: createdComment.createdAt || currentTicket.latestActivityAt,
                      followedByCurrentUser: true,
                  }
                : currentTicket
        )
        setEditingComment(null)
    }

    const updateComment = async (comment: CommentData, input: { message: string; attachments: AttachmentInput }) => {
        await client.product.feedback.comments.updateComment.mutate({
            ticketNumber: number,
            commentId: comment._id.toString(),
            ...input,
        })
        setEditingComment(null)
        reload()
    }

    const deleteComment = async () => {
        if (!commentToDelete) {
            return
        }

        await client.product.feedback.comments.deleteComment.mutate({
            ticketNumber: number,
            commentId: commentToDelete._id.toString(),
        })
        setCommentToDelete(null)
        reload()
    }

    const loadMoreComments = useCallback(async () => {
        if (!comments || isLoadingMoreComments) {
            return
        }

        setIsLoadingMoreComments(true)

        try {
            const result = await client.product.feedback.comments.listComments.query({
                ticketNumber: number,
                limit: COMMENT_PAGE_SIZE,
                offset: comments.length,
                returnCount: true,
                sort: { createdAt: 'asc' },
            })

            setComments([...comments, ...result.comments])
            setCommentCount(result.count ?? comments.length + result.comments.length)
        } finally {
            setIsLoadingMoreComments(false)
        }
    }, [comments, isLoadingMoreComments, number])

    useEffect(() => {
        if (!targetCommentId) {
            scrolledTargetCommentRef.current = null
            return
        }

        if (!comments) {
            return
        }

        const targetCommentLoaded = comments.some((comment) => comment._id.toString() === targetCommentId)

        if (!targetCommentLoaded) {
            scrolledTargetCommentRef.current = null

            if (hasMoreComments && !isLoadingMoreComments) {
                void loadMoreComments()
            }

            return
        }

        if (scrolledTargetCommentRef.current === targetCommentId) {
            return
        }

        scrolledTargetCommentRef.current = targetCommentId
        window.requestAnimationFrame(() => {
            document.getElementById(`comment-${targetCommentId}`)?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
            })
        })
    }, [targetCommentId, comments, hasMoreComments, isLoadingMoreComments, loadMoreComments])

    return (
        <Page
            title={ticket ? ticket.title : t('product.feedback.ticket.loading')}
            description={
                ticket ? (
                    <Toolbar
                        ticket={ticket}
                        canManage={canManage}
                        setTicketMetadataDialogOpen={setTicketMetadataDialogOpen}
                        canCreateComment={canCreateComment}
                        isVoting={isVoting}
                        toggleVote={toggleVote}
                    />
                ) : undefined
            }
            parentLink={{ to: '/product/feedback', search: listSearch }}
            descriptionClassName="max-w-none"
            className="flex min-h-full flex-col space-y-6 pb-8"
        >
            {!ticket && !comments && (
                <Card>
                    <CardHeader>
                        <Skeleton className="h-7 w-1/2" />
                        <Skeleton className="h-4 w-1/4" />
                    </CardHeader>
                    <CardContent>
                        <Skeleton className="h-28 w-full" />
                    </CardContent>
                </Card>
            )}
            {ticket && (
                <>
                    <Header
                        ticket={ticket}
                        isFollowUpdating={isFollowUpdating}
                        setTicketFollowed={setTicketFollowed}
                        canEditTicket={canEditTicket}
                        setTicketDialogOpen={setTicketDialogOpen}
                        canDeleteTicket={canDeleteTicket}
                        setTicketDeleteOpen={setTicketDeleteOpen}
                    />
                    <div className="mx-auto flex w-full max-w-4xl flex-wrap items-center justify-between gap-3">
                        <h2 className="text-xl font-semibold tracking-tight">
                            {t('product.feedback.ticket.comments', { count: ticket.commentsCount })}
                        </h2>
                    </div>
                    <div className="mx-auto w-full max-w-4xl space-y-3">
                        {!comments &&
                            Array.from({ length: 2 }).map((_, index) => (
                                <Card key={`feedback-comment-skeleton-${index.toString()}`} className="gap-0 p-0">
                                    <CardHeader className="p-5">
                                        <Skeleton className="h-4 w-1/3" />
                                        <Skeleton className="h-4 w-full" />
                                        <Skeleton className="h-4 w-3/4" />
                                    </CardHeader>
                                </Card>
                            ))}
                        {comments?.length === 0 && (
                            <Card>
                                <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
                                    <MessageSquareIcon className="text-muted-foreground size-8" />
                                    <div className="font-medium">{t('product.feedback.ticket.empty.title')}</div>
                                    <div className="text-muted-foreground max-w-md text-sm">
                                        {t('product.feedback.ticket.empty.description')}
                                    </div>
                                </CardContent>
                            </Card>
                        )}
                        {comments?.map((comment) => (
                            <Comment
                                key={comment._id.toString()}
                                comment={comment}
                                canManage={canManage}
                                editingComment={editingComment}
                                targetCommentId={targetCommentId}
                                updateComment={updateComment}
                                setEditingComment={setEditingComment}
                                setCommentToDelete={setCommentToDelete}
                            />
                        ))}
                        {hasMoreComments && (
                            <div className="flex justify-center pt-1">
                                <Button
                                    variant="outline"
                                    loading={isLoadingMoreComments}
                                    onClick={() => void loadMoreComments()}
                                >
                                    {t('actions.loadMore')}
                                </Button>
                            </div>
                        )}
                        {canCreateComment && comments && (
                            <InlineContentComposer
                                key={`create-comment-${ticket.number.toString()}`}
                                messageLabel={t('product.feedback.ticket.commentDialog.message')}
                                submitLabel={t('product.feedback.ticket.commentDialog.create')}
                                onSubmit={createComment}
                            />
                        )}
                    </div>
                    <TicketDialog
                        open={ticketDialogOpen}
                        ticket={ticket}
                        onOpenChange={setTicketDialogOpen}
                        onSubmit={updateTicket}
                    />
                    {canManage && (
                        <MetadataDialog
                            open={ticketMetadataDialogOpen}
                            ticket={ticket}
                            onOpenChange={setTicketMetadataDialogOpen}
                            onSubmit={updateTicketMetadata}
                        />
                    )}
                    <DialogPrompt
                        open={ticketDeleteOpen}
                        onOpenChange={setTicketDeleteOpen}
                        title={t('product.feedback.ticket.deleteDialog.title')}
                        message={t('product.feedback.ticket.deleteDialog.message', { title: ticket.title })}
                        confirmLabel={t('product.feedback.ticket.deleteDialog.confirm')}
                        cancelLabel={t('common.cancel')}
                        confirmVariant="destructive"
                        onConfirm={deleteTicket}
                    />
                    <DialogPrompt
                        open={!!commentToDelete}
                        onOpenChange={(open) => {
                            if (!open) {
                                setCommentToDelete(null)
                            }
                        }}
                        title={t('product.feedback.ticket.comment.deleteDialog.title')}
                        message={t('product.feedback.ticket.comment.deleteDialog.message')}
                        confirmLabel={t('product.feedback.ticket.comment.deleteDialog.confirm')}
                        cancelLabel={t('common.cancel')}
                        confirmVariant="destructive"
                        onConfirm={deleteComment}
                    />
                </>
            )}
        </Page>
    )
})
